import React, { useState } from 'react';
import Modal from '@mui/material/Modal';
import GroupIcon from '@mui/icons-material/Group';
import { toast } from 'react-toastify';
import { ErrorToast, SuccessToast } from '../../pure-components/CustomToast';
import API from '../../services/api';

const UserListModal = ({ open, onClose, adminEmail, companyName, adminName }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchUsers = () => {
    if (!open || !adminEmail) return;
    setLoading(true);
    setError(null);
    
    API.getUserList({ filter: { userType: 'CORPORATE', admin: adminEmail } }, '')
      .then(data => setUsers(Array.isArray(data.data) ? data.data : []))
      .catch(() => setError('Failed to fetch users'))
      .finally(() => setLoading(false));
  };

  React.useEffect(() => {
    fetchUsers();
  }, [open, adminEmail]);

  
  const handleUserSuspendToggle = async (userId, currentStatus, userEmail) => {
    try {
      const data = await API.suspendUser(userId, { isSuspended: !currentStatus, email: userEmail });
      if (data.success) {
        toast(({ closeToast }) => (
          <SuccessToast message={`User ${currentStatus ? 'unsuspended' : 'suspended'} successfully`} />
        ), { autoClose: 3000 });
        fetchUsers(); // Refresh the user list
      } else {
        toast(({ closeToast }) => (
          <ErrorToast message={data.msg || 'Failed to update user status'} />
        ));
      }
    } catch (err) {
      toast(({ closeToast }) => (
        <ErrorToast message={err.message || 'Error updating user status'} />
      ));
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-40">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-2xl w-full">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h2 className="text-xl font-bold text-blue-600 flex items-center gap-2"><GroupIcon /> Corporate Users</h2>
              <div className="text-sm text-gray-600 mt-1">
                <span className="font-semibold">Company Name:</span> {companyName || '-'}<br />
                <span className="font-semibold">Admin Name:</span> {adminName || '-'}
              </div>
            </div>
            <button onClick={onClose} className="text-gray-500 hover:text-red-600 text-2xl border-none">&times;</button>
          </div>
          {loading ? (
            <div className="py-8 text-center text-gray-500">Loading...</div>
          ) : error ? (
            <div className="py-8 text-center text-red-500">{error}</div>
          ) : (
            <div style={{ maxHeight: '350px', overflowY: 'auto' }}>
              <table className="min-w-full bg-white rounded-lg shadow text-sm">
                <thead>
                  <tr>
                    <th className="bg-[#f7f8fa] text-gray-700 font-semibold px-4 py-3 text-left">Name</th>
                    <th className="bg-[#f7f8fa] text-gray-700 font-semibold px-4 py-3 text-left">Email</th>
                    <th className="bg-[#f7f8fa] text-gray-700 font-semibold px-4 py-3 text-left">User Type</th>
                    <th className="bg-[#f7f8fa] text-gray-700 font-semibold px-4 py-3 text-left">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {users.length === 0 ? (
                    <tr><td colSpan={4} className="text-center py-8 text-gray-400">No users found.</td></tr>
                  ) : (
                    users.map((user, idx) => (
                      <tr key={user._id || idx} className="hover:bg-gray-50">
                        <td className="px-4 py-2">{user.name || user.first_name || '-'}</td>
                        <td className="px-4 py-2">{user.email || '-'}</td>
                        <td className="px-4 py-2">{user.userType || '-'}</td>
                        <td className="px-4 py-2">
                          <select
                            className={`rounded-full px-3 py-1 text-xs font-semibold border focus:outline-none transition-all duration-200 w-24
                              ${user.isSuspended ? 'bg-red-100 text-red-700 border-red-300' : 'bg-green-100 text-green-700 border-green-300'}`}
                            value={user.isSuspended ? 'Suspended' : 'Active'}
                            onChange={() => handleUserSuspendToggle(user._id, user.isSuspended, user.email)}
                            title="Change status"
                          >
                            <option value="Active">Active</option>
                            <option value="Suspended">Suspended</option>
                          </select>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default UserListModal;